import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import {
  useAcceptInviteMutation,
  useValidateInviteQuery,
} from "../api/queries/orgUsersQueries";
import { resolvePostLoginPath } from "../utils/navigationScopes";
import PageLoading from "./PageLoading";
import { toast } from "sonner";

export default function InviteAcceptPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { isAuthenticated, scopes, switchOrg } = useAuth();
  const { data, isLoading, error } = useValidateInviteQuery(token);
  const acceptMutation = useAcceptInviteMutation();

  useEffect(() => {
    if (token) {
      sessionStorage.setItem("invite_token", token);
    }
  }, [token]);

  useEffect(() => {
    if (data?.org_id) {
      sessionStorage.setItem("invite_org_id", String(data.org_id));
    }
    if (data?.email) {
      sessionStorage.setItem("invite_email", data.email);
    }
  }, [data?.org_id, data?.email]);

  useEffect(() => {
    if (!isAuthenticated || !token || !data || data.expired) return;
    let cancelled = false;
    acceptMutation
      .mutateAsync(token)
      .then(async (result) => {
        if (cancelled) return;
        sessionStorage.removeItem("invite_token");
        const joinedOrgId = result?.org_id ?? data.org_id;
        if (joinedOrgId) {
          try {
            await switchOrg(joinedOrgId);
          } catch {
            /* membership applied; keep current session if switch fails */
          }
        }
        toast.success(`Welcome to ${data.org_name}`);
        navigate(resolvePostLoginPath("/receivables", scopes), { replace: true });
      })
      .catch(() => {
        if (!cancelled) toast.error("Could not accept invitation");
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, token, data]);

  if (!token) {
    return (
      <div className="p-8 text-center text-gray-600">
        Invalid invitation link.
      </div>
    );
  }

  if (isLoading) return <PageLoading />;

  if (error || !data) {
    return (
      <div className="p-8 text-center text-gray-600">
        This invitation is invalid or has expired.
      </div>
    );
  }

  if (data.expired) {
    return (
      <div className="p-8 text-center text-gray-600">
        This invitation has expired. Ask your manager to resend it.
      </div>
    );
  }

  if (!isAuthenticated) {
    navigate("/login", {
      replace: true,
      state: { from: `/invite/accept?token=${encodeURIComponent(token)}` },
    });
    return <PageLoading />;
  }

  return <PageLoading />;
}
